import { Mail, Plus, Trash2, Check, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useEmailNotifications } from '../../../hooks/useEmailNotifications';
import { useAuth } from '../../../contexts/AuthContext';
import { useDesignColors } from '../../../hooks/useDesignColors';

export default function EmailIntegration() {
  const { user } = useAuth();
  const { chatbotPublicId } = useChatbot();
  const { recipients, addRecipient, updateRecipient, removeRecipient, loading, error, initializeRecipients } = useEmailNotifications();
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState('');
  const [settings, setSettings] = useState({
    instantNotifications: true,
    dailyReport: false,
    weeklyReport: false
  });
  const [success, setSuccess] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { colors } = useDesignColors();

  // Initialize recipients on component mount
  useEffect(() => {
    if (chatbotPublicId) {
      initializeRecipients(chatbotPublicId);
    }
  }, [chatbotPublicId, initializeRecipients]);

  // Load existing settings when editing
  useEffect(() => {
    if (editingId) {
      const recipient = recipients.find(r => r.id === editingId);
      if (recipient) {
        setEmail(recipient.email);
        setName(recipient.name || '');
        setSettings({
          instantNotifications: recipient.instantNotifications,
          dailyReport: recipient.dailyReport,
          weeklyReport: recipient.weeklyReport
        });
      }
    } else if (!addingNew && recipients.length > 0 && !editingId) {
      // Load first recipient if not adding new and not editing
      const mainRecipient = recipients[0];
      setEmail(mainRecipient.email);
      setName(mainRecipient.name || '');
      setSettings({
        instantNotifications: mainRecipient.instantNotifications,
        dailyReport: mainRecipient.dailyReport,
        weeklyReport: mainRecipient.weeklyReport
      });
    } else if (addingNew) {
      // Reset form for new recipient
      setEmail('');
      setName('');
      setSettings({
        instantNotifications: true,
        dailyReport: false,
        weeklyReport: false
      });
    }
  }, [recipients, editingId, addingNew]);

  const handleSaveNotifications = async () => {
    if (!email || !chatbotPublicId || !isValidEmail(email)) return;
    
    try {
      if (editingId) {
        // Update existing recipient
        await updateRecipient(editingId, {
          email,
          name,
          instantNotifications: settings.instantNotifications,
          dailyReport: settings.dailyReport,
          weeklyReport: settings.weeklyReport
        }, chatbotPublicId);
        setEditingId(null);
      } else if (addingNew) {
        // Create a new recipient
        await addRecipient({
          email,
          name,
          instantNotifications: settings.instantNotifications,
          dailyReport: settings.dailyReport,
          weeklyReport: settings.weeklyReport
        }, chatbotPublicId);
        setAddingNew(false);
      } else if (recipients.length > 0) {
        // Update first recipient if not adding or editing
        await updateRecipient(recipients[0].id, {
          email,
          name,
          instantNotifications: settings.instantNotifications,
          dailyReport: settings.dailyReport,
          weeklyReport: settings.weeklyReport
        }, chatbotPublicId);
      } else {
        // Create first recipient
        await addRecipient({
          email,
          name,
          instantNotifications: settings.instantNotifications,
          dailyReport: settings.dailyReport,
          weeklyReport: settings.weeklyReport
        }, chatbotPublicId);
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving email notifications:', err);
    }
  };

  const handleRemoveRecipient = async (id: string) => {
    if (!chatbotPublicId) return;
    
    if (editingId === id) {
      setEditingId(null);
    }
    
    await removeRecipient(id, chatbotPublicId);
  };

  const handleSettingChange = (setting: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleAddNew = () => {
    setAddingNew(true);
    setEditingId(null);
    setEmail('');
    setName('');
    setSettings({
      instantNotifications: true,
      dailyReport: false,
      weeklyReport: false
    });
  };

  const handleCancel = () => {
    setAddingNew(false);
    setEditingId(null);
    
    // Reset to first recipient if available
    if (recipients.length > 0) {
      const mainRecipient = recipients[0];
      setEmail(mainRecipient.email);
      setName(mainRecipient.name || '');
      setSettings({
        instantNotifications: mainRecipient.instantNotifications,
        dailyReport: mainRecipient.dailyReport,
        weeklyReport: mainRecipient.weeklyReport
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-6 h-6 text-indigo-400" />
          <h3 className="text-lg font-semibold text-white">Email Notifications</h3>
        </div>

        <div className="space-y-4">
          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-500/10 text-green-400 p-4 rounded-lg flex items-center gap-2">
              <Check className="w-5 h-5" />
              <p>Email notification settings saved successfully!</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Notification Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {email && !isValidEmail(email) && (
              <p className="mt-1 text-sm text-red-400">
                Please enter a valid email address
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Recipient Name (optional)
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter recipient name"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Notification Types</h4>
            
            <label className="flex items-center gap-2 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
              <input 
                type="checkbox" 
                checked={settings.instantNotifications}
                onChange={() => handleSettingChange('instantNotifications')}
                className="rounded border-white/10 bg-white/5 text-indigo-500 focus:ring-indigo-500" 
              />
              <div>
                <span className="text-sm text-white">Instant Notifications</span>
                <p className="text-xs text-gray-400">Get notified immediately when a new lead is detected</p>
              </div>
            </label>
            
            <label className="flex items-center gap-2 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
              <input 
                type="checkbox" 
                checked={settings.dailyReport}
                onChange={() => handleSettingChange('dailyReport')}
                className="rounded border-white/10 bg-white/5 text-indigo-500 focus:ring-indigo-500" 
              />
              <div>
                <span className="text-sm text-white">Daily Report</span>
                <p className="text-xs text-gray-400">Receive a daily summary of all leads and conversations</p>
              </div>
            </label>
            
            <label className="flex items-center gap-2 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
              <input 
                type="checkbox" 
                checked={settings.weeklyReport}
                onChange={() => handleSettingChange('weeklyReport')}
                className="rounded border-white/10 bg-white/5 text-indigo-500 focus:ring-indigo-500" 
              />
              <div>
                <span className="text-sm text-white">Weekly Report</span>
                <p className="text-xs text-gray-400">Receive a weekly summary with performance analytics</p>
              </div>
            </label>
          </div>

          <div className="flex gap-3 mt-4">
            {(addingNew || editingId) && (
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 bg-white/5 text-gray-300 rounded-lg hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
            )}
            
            <button
              onClick={handleSaveNotifications}
              disabled={loading || !isValidEmail(email)}
              className="flex-1 bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-lg hover:bg-indigo-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  {editingId ? 'Update Recipient' : 
                   addingNew ? 'Add Recipient' : 
                   recipients.length > 0 ? 'Update Notifications' : 'Enable Notifications'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Recipients List */}
      {recipients.length > 0 && (
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Notification Recipients</h3>
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Recipient
            </button>
          </div>
          
          <div className="space-y-4">
            {recipients.map((recipient) => (
              <div 
                key={recipient.id} 
                className={`flex items-center justify-between p-3 rounded-lg ${
                  editingId === recipient.id 
                    ? 'bg-indigo-500/10 border border-indigo-500/30' 
                    : 'bg-white/5'
                }`}
              >
                <div>
                  <p className="text-white">{recipient.email}</p>
                  {recipient.name && <p className="text-sm text-gray-400">{recipient.name}</p>}
                  <div className="flex gap-2 mt-1">
                    {recipient.instantNotifications && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-500/20 text-indigo-400">Instant</span>
                    )}
                    {recipient.dailyReport && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-500/20 text-indigo-400">Daily</span>
                    )}
                    {recipient.weeklyReport && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-500/20 text-indigo-400">Weekly</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingId(recipient.id)}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button
                    onClick={() => handleRemoveRecipient(recipient.id)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Email Templates */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Email Templates</h3>
        
        <div className="space-y-6">
          <div className="p-4 rounded-lg bg-white/5">
            <h4 className="text-white font-medium mb-2">Instant Notification</h4>
            <div className="bg-black/20 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-white">New Lead Detected</p>
                  <p className="text-xs text-gray-400">From: Botmaker Agency</p>
                </div>
              </div>
              <div className="text-sm text-gray-300">
                <p>A new lead has been detected in your chatbot conversation.</p>
                <p className="mt-2">Contact: <span className="text-indigo-400">example@email.com</span></p>
                <p className="mt-2">First message: "I'm interested in your services..."</p>
                <p className="mt-4 text-xs text-gray-400">Click to view the full conversation in your dashboard.</p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-white/5">
            <h4 className="text-white font-medium mb-2">Daily Report</h4>
            <div className="bg-black/20 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-white">Daily Chatbot Report</p>
                  <p className="text-xs text-gray-400">From: Botmaker Agency</p>
                </div>
              </div>
              <div className="text-sm text-gray-300">
                <p>Here's your daily summary for [Chatbot Name]:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>New conversations: 12</li>
                  <li>New leads collected: 3</li>
                  <li>Average conversation duration: 2m 45s</li>
                </ul>
                <p className="mt-4 text-xs text-gray-400">View detailed analytics in your dashboard.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}